// core/stores/feedbackStore.ts — Zustand store for the product feedback board.
//
// Deliberately starts EMPTY — unlike the older stores in this folder, nothing is
// seeded at module init. Shipping hardcoded feedback would show fabricated items
// to every visitor until hydration replaced them. Services mutate this store;
// hooks read it reactively. Components must use hooks, not this store directly.
//
// The store holds FeedbackListItem — the shape the LIST endpoint returns.
// FeedbackItem (the DETAIL shape) is a strict superset (adds comments,
// roadmap, watchers, related, attachments, activity), so a detail fetch's
// result is always assignable here. upsertItem MERGES rather than replaces so
// a later list refetch (which carries only the list-shape keys) can never
// clobber a previously-cached item's detail-only fields — see FeedbackItemHook.

import { create } from 'zustand';
import type { FeedbackItem, FeedbackListItem } from '@/core/models/feedback';

/** Hydration state — lets pages tell "still loading" from "genuinely empty". */
export type FeedbackLoadState = 'idle' | 'loading' | 'ready' | 'error';

interface FeedbackState {
  items: FeedbackListItem[];
  loadState: FeedbackLoadState;

  setItems: (items: FeedbackListItem[]) => void;
  setLoadState: (state: FeedbackLoadState) => void;
  /** Insert or merge one item, preserving list position when it already exists. */
  upsertItem: (item: FeedbackListItem) => void;
  /** Merges a FIELD-SCOPED patch onto whatever the CURRENT entry for `id`
   * looks like (a no-op if the id isn't cached) — the primitive behind every
   * optimistic update / rollback in feedbackService, so a patch only ever
   * touches the keys it names and always applies on top of the latest known
   * state, never a stale snapshot (2026-08-10 adversarial review, item 3). */
  patchItem: (id: string, patch: Partial<FeedbackItem>) => void;
  /** Remove an item from the local list (staff hide). */
  dropItem: (id: string) => void;
}

export const useFeedbackStore = create<FeedbackState>()((set) => ({
  items: [],
  loadState: 'idle',

  // Membership/order must match the query result EXACTLY (this is the
  // board's visible list — a stale leftover from a previous filter must
  // never linger), but each incoming list-shaped item is merged onto any
  // existing cached entry for that id so a previously-loaded detail (roadmap,
  // comments, etc.) survives a board list refetch instead of reverting to the
  // bare list shape (2026-08-10 adversarial review, item 2).
  setItems: (items) =>
    set((s) => ({
      items: items.map((incoming) => {
        const existing = s.items.find((i) => i.id === incoming.id);
        return existing ? { ...existing, ...incoming } : incoming;
      }),
    })),

  setLoadState: (loadState) => set({ loadState }),

  upsertItem: (item) =>
    set((s) =>
      s.items.some((i) => i.id === item.id)
        ? { items: s.items.map((i) => (i.id === item.id ? { ...i, ...item } : i)) }
        : { items: [item, ...s.items] },
    ),

  patchItem: (id, patch) =>
    set((s) => ({
      items: s.items.map((i) => (i.id === id ? { ...i, ...patch } : i)),
    })),

  dropItem: (id) => set((s) => ({ items: s.items.filter((i) => i.id !== id) })),
}));
